import React from "react";

import WComponent from "../../../common/WComponent";

/**
 * A block of prose that can be rewritten in place, one paragraph at a time.
 *
 * What is being typed is held here and sent on when the paragraph is left, rather than saved on
 * every keystroke. Saving per keystroke means the box is showing a value that is still travelling
 * back from the server, so React redraws it from the stale answer and the characters typed in the
 * meantime disappear — typing "Test" leaves "Tt".
 *
 * The draft stands in for the stored text until the caller replaces it, so the words stay put while
 * the save is in flight. Remount the component (a `key` that changes with the mode) to abandon a
 * draft and go back to what is stored.
 *
 * Leaving the screen commits too. Blur normally fires first, but a tab changed from the keyboard or
 * in code can unmount the box while it still holds the only copy of what was typed, and losing an
 * edit that way is the same failure as losing the keystrokes.
 */
class ToolsEditableParagraphs extends WComponent {
	state = { drafts: null };

	readParagraphs = () => {
		const { paragraphs } = this.props;
		const drafts = this.state.drafts;
		return drafts || paragraphs || [];
	};

	onParagraphTyped = ({ index, text }) => {
		const current = this.readParagraphs();
		const drafts = current.map((paragraph, position) => (position === index ? text : paragraph));
		this.update({ drafts });
	};

	commitParagraphs = async () => {
		const { paragraphs, onCommit } = this.props;
		const drafts = this.state.drafts;
		if (!drafts) return;

		const stored = paragraphs || [];
		const unchanged = drafts.length === stored.length && drafts.every((paragraph, position) => paragraph === stored[position]);
		if (unchanged) return;

		if (onCommit) await onCommit({ paragraphs: drafts });
	};

	componentWillUnmount() {
		this.commitParagraphs();
	}

	renderParagraph = (paragraph, index) => {
		const { paragraphClassName } = this.props;
		const linesNeeded = Math.max(2, Math.ceil(String(paragraph || "").length / 95));

		return (
			<textarea
				key={index}
				className={paragraphClassName}
				value={paragraph}
				rows={linesNeeded}
				onChange={e => this.onParagraphTyped({ index, text: e.target.value })}
				onBlur={this.commitParagraphs}
				spellCheck={true}
			/>
		);
	};

	render() {
		const paragraphs = this.readParagraphs();
		return <>{paragraphs.map(this.renderParagraph)}</>;
	}
}

export default ToolsEditableParagraphs;
